import { useSyncExternalStore } from 'react';
import { notificationPermission, type PermissionState } from './subscription';

export interface PushState {
  permission: PermissionState;
  /** true after POST /api/push/subscribe succeeded in this page load. */
  subscribed: boolean;
}

type Listener = () => void;

let state: PushState = { permission: 'unsupported', subscribed: false };
let initialised = false;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of [...listeners]) l();
}

function ensureInitialised(): void {
  if (initialised) return;
  initialised = true;
  state = { permission: notificationPermission(), subscribed: false };
}

export const pushStore = {
  get: (): PushState => {
    ensureInitialised();
    return state;
  },
  /** Re-reads Notification.permission (after a prompt) and records the subscription outcome. */
  update: (patch: Partial<PushState> = {}): void => {
    ensureInitialised();
    state = { ...state, permission: notificationPermission(), ...patch };
    emit();
  },
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** Tests only. */
  reset: (): void => {
    initialised = false;
    state = { permission: 'unsupported', subscribed: false };
    emit();
  },
};

export function usePushState(): PushState {
  return useSyncExternalStore(pushStore.subscribe, pushStore.get, pushStore.get);
}
